// OW.js
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import './OW.css';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const CylinderChart = ({ data }) => {
    const svgRef = useRef();

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const width = 130;
        const paddingTop = 50;
        const cylinderHeight = 245;

        const totalDepth = parseFloat(data.deep) || 0;

        // 分層群組
        const gPipe = svg.append('g').attr('class', 'pipe'); // 井管、填色等
        const gWater = svg.append('g').attr('class', 'water'); // 水位填色
        const gOver = svg.append('g').attr('class', 'overlays'); // 警戒/行動線(最上層)

        // 座標：0 在井口，往下增加
        const scale = d3
            .scaleLinear()
            .domain([0, totalDepth])
            .range([paddingTop, paddingTop + cylinderHeight]);

        // 外框（井管）
        gPipe
            .append('rect')
            .attr('x', 50)
            .attr('y', paddingTop)
            .attr('width', width)
            .attr('height', cylinderHeight)
            .attr('rx', 10)
            .attr('ry', 10)
            .attr('stroke', 'black')
            .attr('fill', 'none');

        var warningUp = parseFloat(data.warning_up);
        var actionUp = parseFloat(data.action_up);
        // 警戒/行動線
        if (data.water_level_head) {
            warningUp = data.water_level_head - warningUp;
            actionUp = data.water_level_head - actionUp;
        }

        const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

        if (Number.isFinite(warningUp)) {
            const y = scale(clamp(warningUp, 0, totalDepth));
            gOver
                .append('line')
                .attr('x1', 50)
                .attr('y1', y)
                .attr('x2', 180)
                .attr('y2', y)
                .attr('stroke', 'orange')
                .attr('stroke-width', 2.5) // 加粗
                .attr('stroke-dasharray', '4 2');
            gOver
                .append('text')
                .attr('x', 185)
                .attr('y', y + 4)
                .attr('fill', 'orange')
                .attr('font-size', 10)
                .text(`-${warningUp.toFixed(2)}m`);
        }

        if (Number.isFinite(actionUp)) {
            const y = scale(clamp(actionUp, 0, totalDepth));
            gOver
                .append('line')
                .attr('x1', 50)
                .attr('y1', y)
                .attr('x2', 180)
                .attr('y2', y)
                .attr('stroke', 'red')
                .attr('stroke-width', 2.5) // 加粗
                .attr('stroke-dasharray', '4 2');
            gOver
                .append('text')
                .attr('x', 185)
                .attr('y', y + 4)
                .attr('fill', 'red')
                .attr('font-size', 10)
                .text(`-${actionUp.toFixed(2)}m`);
        }

        // 有水位才填色
        const hasValidData =
            data.d_level !== undefined &&
            data.d_level !== null &&
            !isNaN(parseFloat(data.d_level));
        if (hasValidData) {
            const dLevel = clamp(parseFloat(data.d_level), 0, totalDepth);
            const liquidY = scale(dLevel);
            const liquidHeight = paddingTop + cylinderHeight - liquidY;

            gWater
                .append('rect')
                .attr('x', 50)
                .attr('y', liquidY)
                .attr('width', width)
                .attr('height', liquidHeight)
                .attr('rx', 10)
                .attr('ry', 10)
                .attr('fill', 'lightblue');

            const centerX = 50 + width / 2;
            gWater
                .append('text')
                .attr('x', centerX)
                .attr('y', liquidY - 5)
                .attr('fill', 'black')
                .attr('text-anchor', 'middle')
                .text(`-${dLevel.toFixed(2)}m`);
        }

        // 確保 overlays 在最上層
        gOver.raise();
    }, [data]);

    return (
        <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox="0 0 230 300"
            preserveAspectRatio="xMidYMid meet"
        />
    );
};

export default function OW({ water_level }) {
    const navigate = useNavigate();
    const [tooltipData, setTooltipData] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

    return (
        <div className="grid-container-ow">
            {water_level.map((item, idx) => (
                <div
                    className="grid-item-ow"
                    key={idx}
                    onMouseEnter={(e) => {
                        setTooltipData(item);
                        setTooltipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setTooltipData(null)}
                    onClick={() => navigate(`/device/${item.name}`)}
                >
                    <div className="chart-box">
                        <CylinderChart data={item} />
                    </div>
                    <div className="sensor-name">
                        <div className="sensor-name">
                            <span className="sensor-label">{item.name}</span>
                            <span className={`badge${item.d_level ? ' is-hidden' : ''}`}>
                                無資料
                            </span>
                        </div>
                    </div>
                </div>
            ))}

            {tooltipData && (
                <div
                    className="tooltip"
                    style={{
                        position: 'fixed',
                        left: `${tooltipPos.x + 10}px`,
                        top: `${tooltipPos.y + 10}px`,
                        opacity: 1,
                        pointerEvents: 'none',
                    }}
                >
                    <div>測量時間: {tooltipData.obs_time}</div>
                    <div>
                        深度:{' '}
                        {tooltipData.d_level
                            ? `${(parseFloat(tooltipData.d_level) * -1).toFixed(2)} m`
                            : '無資料'}
                    </div>
                    <div>
                        水位高程:{' '}
                        {Number.isFinite(parseFloat(tooltipData.water_level_head)) &&
                        Number.isFinite(parseFloat(tooltipData.d_level))
                            ? (
                                  parseFloat(tooltipData.water_level_head) -
                                  parseFloat(tooltipData.d_level)
                              ).toFixed(2)
                            : '—'}{' '}
                        m
                    </div>
                    <div>
                        警戒高程:{' '}
                        {Number.isFinite(parseFloat(tooltipData.water_level_head))
                            ? ''
                            : '-'}{' '}
                        {tooltipData.warning_up} m
                    </div>
                    <div>
                        行動高程:{' '}
                        {Number.isFinite(parseFloat(tooltipData.water_level_head))
                            ? ''
                            : '-'}{' '}
                        {tooltipData.action_up} m
                    </div>
                </div>
            )}
        </div>
    );
}
